import { FC } from "preact/compat";
import { useRef } from "preact/hooks";
import { useSignal, useSignalEffect } from "@preact/signals";
import { format, isSameMonth } from "date-fns";
import { useApp } from "~/core";
import { createDonutChart } from "~/chart";
import { getReportProgress } from "~/chart-data";
import { useAppTime, useCanvasInit, useLocal } from "~/hooks";
import { formatHours } from "~/utils";
import { useMobileDashboard } from "../../context";
import { SingleWidgetProps } from "../interfaces";
import FunctionsIcon from "@material-design-icons/svg/outlined/functions.svg";
import TimeIcon from "@material-design-icons/svg/outlined/schedule.svg";
import Loader from "~/assets/loader.svg";
import cn from "classnames";

const EMPTY_CHART_DATA: ReturnType<typeof getReportProgress> = {
    colors: [],
    labels: [],
    values: [],
    total: 0,
    reported: 0,
};

const getTargetDate = (
    selectedDate: Date,
    selectedDay: Date | null,
    today: Date,
    mode: "month" | "day",
) => {
    if (mode === "day") {
        return selectedDay ?? selectedDate;
    }

    if (isSameMonth(selectedDate, today)) {
        return today;
    }

    const eom = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth() + 1,
        0,
    );

    return eom;
};

const Widget: FC<SingleWidgetProps> = ({ className }) => {
    const { api, mobile } = useApp();
    const { loading, mode, selectedDate, selectedDay } = useMobileDashboard();
    const storage = useLocal("v1_m-dashboard_progress", {
        accumulated: false,
    });
    const chartRef = useRef<ReturnType<typeof createDonutChart> | null>(null);
    const chartData = useSignal(EMPTY_CHART_DATA);
    const today = useAppTime("day");
    const ref = useCanvasInit((canvas) => {
        chartRef.current = createDonutChart({
            canvas,
            data: chartData.peek(),
            gradient: [1, 2],
        });

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    });

    useSignalEffect(() => {
        const isLoading = loading.value;
        const date = selectedDate.value;
        const day = selectedDay.value;
        const mod = mode.value;
        const acc = storage.value.accumulated;
        const now = today.value;

        void api.time.report.reports.value;

        if (isLoading) {
            return;
        }

        chartData.value = getReportProgress(
            getTargetDate(date, day, now, mod),
            acc ? "accumulated" : "day",
        );

        if (chartRef.current) {
            chartRef.current.update(chartData.peek());
        }
    });

    const accumulated = storage.value.accumulated;
    const data = chartData.value;
    const hasData = data.values.length !== 0;
    const date = getTargetDate(
        selectedDate.value,
        selectedDay.value,
        today.value,
        mode.value,
    );

    return (
        <div className={className}>
            <div className="flex justify-between items-center border-b border-gray-200 py-1 px-2">
                <div className="flex items-center gap-x-2 text-gray-500 font-bold py-1">
                    <TimeIcon width="20" height="20" className="fill-current" />
                    <span>Report Progress</span>
                </div>
                <label
                    className={cn(
                        "flex rounded-full p-0.5 border transition duration-200 gap-x-1 fill-current items-center",
                        accumulated
                            ? "bg-sky-600 text-white border-sky-600"
                            : "border-gray-300 text-gray-400",
                    )}
                >
                    <FunctionsIcon width="20" height="20" />
                    <input
                        type="checkbox"
                        className="opacity-0 size-0 absolute"
                        checked={accumulated}
                        onClick={({ currentTarget }) => {
                            storage.value = {
                                accumulated: currentTarget.checked,
                            };
                            mobile.impact("light");
                        }}
                    />
                </label>
            </div>
            <div className="relative size-60 mx-auto flex items-center justify-center">
                <canvas ref={ref} className="size-full" />
                {loading.value ? (
                    <div className="absolute">
                        <Loader
                            width="24"
                            height="24"
                            className="text-sky-500"
                        />
                    </div>
                ) : (
                    <div className="absolute text-center flex flex-col gap-y-0.5">
                        {hasData ? (
                            <>
                                {accumulated ? (
                                    <p className="text-sm">
                                        Accumulated until{" "}
                                        {format(date, "dd MMM")}
                                    </p>
                                ) : (
                                    <p className="text-sm">
                                        {format(date, "dd MMM")}
                                    </p>
                                )}
                                <p className="font-bold">
                                    Contracted: {formatHours(data.total)}
                                </p>
                                <p className="font-bold">
                                    Reported: {formatHours(data.reported)}
                                </p>
                            </>
                        ) : (
                            <p className="text-sm text-gray-500">No data</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Widget;
